"use client";
import { useState, useEffect } from "react";
import { PotentialSolution } from "@/types/potentialSolution";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toast } from "react-toastify";
import Link from "next/link";
import { DeleteDialog } from "../Common/DeleteDialog";

const DraftBlogsPage = () => {
  const [potentialSolutions, setPotentialSolutions] = useState<
    PotentialSolution[]
  >([]);
  const [deploying, setDeploying] = useState(false);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDrafts = async () => {
      const token = localStorage.getItem("yardstickAccessToken") || "";
      try {
        const response = await fetch("/api/potential-solutions", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const res = await response.json();

        if (!response.ok) {
          console.log(response);
          if (response.status === 401) {
            toast.error(res.error, {
              toastId: "auth-error",
            });
            return;
          }
          throw new Error(res.error);
        }

        setPotentialSolutions(res.data);
      } catch (e) {
        console.log(e);
        setError("Couldn't fetch potential solutions.");
        toast.error(`Error: ${e.message}`, {
          toastId: "potential-solution-fetch-error",
        });
      } finally {
        setLoading(false);
      }
    };
    fetchDrafts();
  }, []);

  const handleDeploy = async () => {
    setDeploying(true);
    try {
      const response = await fetch("/api/deploy", {
        method: "POST",
      });
      const data = await response.json();

      if (response.ok) {
        console.log(data.message);
        toast.success("Deployment triggered successfully", {
          toastId: "deploy-success",
        });
      } else {
        toast.error(`Deployment failed: ${data.error}`, {
          toastId: "deploy-error",
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(`An error occurred: ${(error as Error).message}`, {
        toastId: "deploy-error",
      });
    } finally {
      setDeploying(false);
    }
  };

  const createSamplePotentialSolution = async () => {
    const token = localStorage.getItem("yardstickAccessToken") || "";
    setCreating(true);
    try {
      const response = await fetch("/api/potential-solutions/sample", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const res = await response.json();

      if (!response.ok) {
        console.log(response);
        if (response.status === 401) {
          toast.error(res.error, {
            toastId: "auth-error",
          });
          return;
        }
        throw new Error(res.error);
      }

      setPotentialSolutions((p) => [...p, res.data]);
      toast.success("Potential solution created successfully.", {
        toastId: "sample-potential-solution-created-success",
      });
    } catch (e) {
      console.log(e);
      toast.error(`Error: ${e.message}`, {
        toastId: "sample-potential-solution-creation-error",
      });
    } finally {
      setCreating(false);
    }
  };

  const handlePotentialSolutionDelete = async (id: string) => {
    try {
      const response = await fetch(`/api/potential-solutions/${id}`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("yardstickAccessToken")}`,
        },
      });
      const data = await response.json();

      if (response.ok) {
        toast.success("Potential solution deleted successfully");
      } else {
        console.log(response);
        if (response.status === 401) {
          toast.error("Error: You're not logged in.", {
            toastId: "auth-error",
          });
          return;
        }
        toast.error(`Deletion failed: ${data.error}`, {
          toastId: `delete-error-${id}`,
        });
      }
    } catch (error) {
      console.log(error);
      toast.error(`An error occurred: ${(error as Error).message}`);
    }
  };

  return (
    <div className="flex items-center justify-center">
      <div className="mt-20 flex h-fit max-w-[1500px] flex-col gap-10 p-5 sm:p-20">
        {loading ? (
          <p>Fetching Potential Solutions...</p>
        ) : error ? (
          <p>{error}</p>
        ) : (
          <div className="flex flex-col gap-10">
            <div className="flex items-center justify-between gap-5 sm:min-w-[575px]">
              <h1 className="text-3xl font-bold sm:text-4xl">Potential Solutions</h1>
              <button
                className="rounded bg-primary p-2 text-white transition-all duration-300 ease-in-out hover:bg-primary/80 dark:bg-primary1"
                onClick={handleDeploy}
                disabled={deploying}
              >
                {deploying ? "Uploading..." : "Upload To Vercel"}
              </button>
            </div>
            <div className="flex justify-end">
              <button
                className="rounded bg-primary p-2 text-white transition-all duration-300 ease-in-out hover:bg-primary/80 dark:bg-primary1"
                onClick={createSamplePotentialSolution}
                disabled={creating}
              >
                {creating ? "Creating..." : "New Potential Solution"}
              </button>
            </div>

            <table className="mt-5 w-full table-auto border-collapse">
              <thead>
                <tr className="border-b border-body-color dark:border-body-color-dark">
                  <th className="w-[40px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    S.No
                  </th>
                  <th className="px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Title
                  </th>
                  <th className="w-[125px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Category
                  </th>
                  <th className="w-[70px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Status
                  </th>
                  <th className="w-[70px] px-2 py-3 text-left text-sm font-light text-body-color dark:text-body-color-dark">
                    Delete
                  </th>
                </tr>
              </thead>
              <tbody>
                {potentialSolutions.map((potentialSolution, i) => (
                  <tr
                    key={potentialSolution.id}
                    className="border-b border-body-color text-sm font-light hover:bg-purple-900 hover:text-white dark:border-body-color-dark"
                  >
                    <td className="px-2 py-3 dark:text-white sm:py-4">
                      {i + 1}
                    </td>
                    <td className="px-2 py-3 text-xs dark:text-white">
                      <Link
                        href={`/edit-potential-solutions/${potentialSolution.id}`}
                        passHref
                      >
                        <span className="text-left">
                          {potentialSolution.title}
                        </span>
                      </Link>
                    </td>
                    <td className="px-2 py-3 text-xs dark:text-white">
                      {potentialSolution.category}
                    </td>
                    <td
                      className={`px-2 py-3 text-xs ${potentialSolution.status === "Draft" ? "text-red-500" : potentialSolution.status === "InReview" ? "text-blue-500" : "text-green-500"}`}
                    >
                      {potentialSolution.status === "InReview"
                        ? "In Review"
                        : potentialSolution.status}
                    </td>
                    <td className="flex justify-center px-2 py-3 text-red-500 sm:py-4">
                      <DeleteDialog
                        item={"potential solution"}
                        onDelete={() =>
                          handlePotentialSolutionDelete(potentialSolution.id)
                        }
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ToastContainer />
      </div>
    </div>
  );
};

export default DraftBlogsPage;
